import { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { CompactTable, type CompactTableColumn } from "@/components/common/CompactTable";
import { SectionCard } from "@/components/common/SectionCard";
import { TabContent } from "@/components/common/PageLayout";
import { StatusChip } from "@/components/common/fields/StatusChipField";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { Reimbursement, ResourceName } from "@/types";
import { ColorType, UIComponentSize } from "@/types/shared/uiComponents";
import { formatUSD } from "@/utils/formatters/currencyFormatter";
import { formatLongDate } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { getStatusColor, type StatusColor } from "@/utils/helpers/getStatusColor";
import { reimbursementDate } from "@/resources/clients/components/claims-summary/utils/reportingPeriod";
import { useStoplossClaimContext } from "../hooks/useStoplossClaimContext";
import { LinkReimbursementDialog } from "./LinkReimbursementDialog";

const methodLabel = (r: Reimbursement): string => {
  if (r.payment_method) return formatToTitleCase(String(r.payment_method));
  if (r.eft) return "EFT";
  if (r.check_number) return "Check";
  return "—";
};

const referenceLabel = (r: Reimbursement): string => r.payment_reference || r.check_number || "—";

const columns: CompactTableColumn<Reimbursement>[] = [
  {
    key: "date",
    label: "Payment date",
    width: "130px",
    render: (r) => <Typography variant="body2">{formatLongDate(reimbursementDate(r) ?? undefined)}</Typography>,
  },
  {
    key: "method",
    label: "Method",
    width: "90px",
    render: (r) => <Typography variant="body2">{methodLabel(r)}</Typography>,
  },
  {
    key: "reference",
    label: "Reference",
    width: "1fr",
    render: (r) => (
      <Typography variant="body2" noWrap>
        {referenceLabel(r)}
      </Typography>
    ),
  },
  {
    key: "status",
    label: "Status",
    width: "120px",
    render: (r) => (
      <StatusChip
        label={formatToTitleCase(r.status || "—")}
        color={getStatusColor(r.status as unknown as StatusColor) ?? ColorType.Default}
        size={UIComponentSize.Small}
      />
    ),
  },
  {
    key: "amount",
    label: "Amount",
    width: "130px",
    align: "right",
    render: (r) => (
      <Typography variant="body2" fontWeight={500}>
        {typeof r.reimbursement_amount === "number" ? formatUSD(r.reimbursement_amount) : "—"}
      </Typography>
    ),
  },
];

/** Reimbursements linked to this claim (#7585): the client summary's nested rows, promoted to a tab, with a link action. */
export const StoplossClaimReimbursementsTab = () => {
  const { claim, displayClaimantId, reimbursements, reimbursedTotal, reimbursementsLoading, reimbursementsError } =
    useStoplossClaimContext();
  const { canEdit } = useResourcePermissions(ResourceName.REIMBURSEMENTS);
  const [linkOpen, setLinkOpen] = useState(false);
  const claimAmount = claim.total_paid_amount ?? 0;
  const outstanding = Math.max(claimAmount - reimbursedTotal, 0);

  const linkAction = canEdit ? (
    <Button size="small" onClick={() => setLinkOpen(true)} sx={{ textTransform: "none" }}>
      Link a reimbursement
    </Button>
  ) : undefined;

  const sorted = [...reimbursements].sort((a, b) =>
    (reimbursementDate(b) ?? "").localeCompare(reimbursementDate(a) ?? "")
  );

  return (
    <TabContent>
      <SectionCard
        compact
        title="Reimbursements"
        badge={reimbursements.length || undefined}
        action={linkAction}
        noPadding
      >
        {reimbursementsLoading ? (
          <Typography variant="body2" color="text.disabled" sx={{ p: 2 }}>
            Loading reimbursements…
          </Typography>
        ) : reimbursementsError ? (
          <Typography variant="body2" color="error" sx={{ p: 2 }}>
            Couldn’t load reimbursements for this claim.
          </Typography>
        ) : reimbursements.length === 0 ? (
          <Box sx={{ py: 5, px: 2, textAlign: "center" }} data-testid="reimbursements-empty">
            <Typography variant="body2" color="text.secondary">
              No reimbursements linked to this claim yet.
            </Typography>
            <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
              Carrier payments link here when a remittance loads
              {canEdit ? ", or link one the claimant already has." : "."}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ overflowX: "auto", "& > *": { minWidth: 640 } }}>
              <CompactTable
                data={sorted}
                columns={columns}
                rowLink={(r) => `/reimbursements/${r.id}/show`}
                rowsPerPage={25}
              />
            </Box>
            <Box
              data-testid="reimbursements-footer"
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
                px: 2,
                py: 1.25,
                borderTop: "1px solid",
                borderColor: "divider",
                flexWrap: "wrap",
              }}
            >
              <Typography variant="body2" fontWeight={600}>
                Reimbursed {formatUSD(reimbursedTotal)}, outstanding {formatUSD(outstanding)} of the{" "}
                {formatUSD(claimAmount)} claim amount
              </Typography>
              <Typography variant="body2" fontWeight={600}>
                {formatUSD(reimbursedTotal)}
              </Typography>
            </Box>
          </>
        )}
      </SectionCard>
      {canEdit && (
        <LinkReimbursementDialog
          open={linkOpen}
          onClose={() => setLinkOpen(false)}
          claim={claim}
          claimantId={displayClaimantId}
        />
      )}
    </TabContent>
  );
};
